import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import React from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import { PaymentNoticeData, yen } from '~/javascripts/admin/utils/paymentNotice';

// 支払通知書のビュー。文字列連結ではなく JSX + styled-components で組むことで、
// 構造・スタイルの見通しとテスト容易性を確保する(React が自動エスケープするため手動エスケープは不要)。
// html2canvas は live DOM の computed style を読むため、styled-components のクラス由来スタイルでも問題なく反映される。

// 参考デザイン(青基調の請求書)のテイストに合わせた配色。
const ACCENT = '#2b6cb0';
const ACCENT_DARK = '#1f4e79';
const BORDER = '#b6cbe6';
const HEADER_BG = '#eaf2fb';
const MUTED = '#5a6b7b';

// A4 縦 @ ~96dpi。オフスクリーン描画時のキャプチャ幅。
const CAPTURE_WIDTH_PX = 794;

const Page = styled.div`
  box-sizing: border-box;
  width: 100%;
  padding: 44px 52px;
  background: #ffffff;
  color: #1f2d3d;
  font-family: 'Hiragino Kaku Gothic ProN', 'Hiragino Sans', 'Yu Gothic', 'Meiryo', sans-serif;
`;

const HeaderTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 36px;
`;

const TitleBadge = styled.span`
  display: inline-block;
  border: 2px solid ${ACCENT};
  border-radius: 3px;
  padding: 7px 26px;
  font-size: 22px;
  font-weight: bold;
  letter-spacing: 0.4em;
  color: ${ACCENT_DARK};
`;

const MetaCell = styled.td`
  width: 24%;
  text-align: right;
  vertical-align: top;
  font-size: 12px;
  line-height: 1.9;
  color: ${MUTED};
`;

const PartyTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 26px;
`;

const RecipientCell = styled.td`
  width: 58%;
  vertical-align: top;
  border-bottom: 2px solid ${ACCENT};
  padding-bottom: 8px;
`;

const IssuerCell = styled.td`
  width: 38%;
  vertical-align: top;
  text-align: right;
`;

const RegistrationNumber = styled.div`
  font-size: 12px;
  color: ${MUTED};
  margin-top: 4px;
`;

const InfoTable = styled.table`
  width: 58%;
  border-collapse: collapse;
  margin-bottom: 18px;
`;

const InfoLabelCell = styled.td`
  padding: 7px 0;
  color: ${MUTED};
  font-size: 12.5px;
  width: 120px;
  vertical-align: top;
`;

const InfoValueCell = styled.td`
  padding: 7px 0;
  font-size: 12.5px;
  border-bottom: 1px solid #e4e9f0;
`;

const AmountBand = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 24px;
  background: ${HEADER_BG};
  border: 1px solid ${BORDER};
`;

const AmountLabelCell = styled.td`
  padding: 14px 18px;
  font-size: 14px;
  font-weight: bold;
  color: ${ACCENT_DARK};
  width: 140px;
`;

const AmountValueCell = styled.td`
  padding: 14px 18px;
  font-size: 24px;
  font-weight: bold;
  color: ${ACCENT_DARK};
`;

const DetailTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const DetailHead = styled.th<{ $width?: string }>`
  border: 1px solid ${BORDER};
  background: ${HEADER_BG};
  padding: 9px 14px;
  font-size: 12.5px;
  font-weight: 600;
  color: ${ACCENT_DARK};
  text-align: ${({ $width }) => ($width ? 'center' : 'left')};
  ${({ $width }) => ($width ? `width:${$width};` : '')}
`;

const DetailCell = styled.td<{
  $align?: 'left' | 'center' | 'right';
  $emphasize?: boolean;
  $muted?: boolean;
  $width?: string;
}>`
  border: 1px solid ${BORDER};
  padding: 9px 14px;
  font-size: 12.5px;
  text-align: ${({ $align }) => $align ?? 'left'};
  ${({ $emphasize }) => ($emphasize ? 'font-weight:bold;background:#f6f9fd;' : '')}
  ${({ $muted }) => ($muted ? `color:${MUTED};` : '')}
  ${({ $width }) => ($width ? `width:${$width};` : '')}
`;

type DetailRowProps = {
  label: string;
  rate: string;
  amount: string;
  emphasize?: boolean;
  muted?: boolean;
};

const DetailRow = ({ label, rate, amount, emphasize, muted }: DetailRowProps) => (
  <tr>
    <DetailCell $align="left" $emphasize={emphasize} $muted={muted}>
      {label}
    </DetailCell>
    <DetailCell $align="center" $emphasize={emphasize} $muted={muted} $width="56px">
      {rate}
    </DetailCell>
    <DetailCell $align="right" $emphasize={emphasize} $muted={muted} $width="140px">
      {amount}
    </DetailCell>
  </tr>
);

const InfoRow = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <tr>
    <InfoLabelCell>{label}</InfoLabelCell>
    <InfoValueCell>{children}</InfoValueCell>
  </tr>
);

export const PaymentNotice = ({ data: d }: { data: PaymentNoticeData }) => {
  // 相殺行のラベル。充当先の請求データ(相手側)の期間を明示する。期間が取れない場合は
  // 期間表記を省く(支払側自身の期間を出すと不整合になるため出さない)。
  const offsetLabel = (from: string, to: string) =>
    from && to ? `請求額の相殺（${from} ~ ${to}）` : '請求額の相殺';

  // 振込先口座は2行で出す: 1行目=銀行・支店, 2行目=種別・口座番号・名義。
  const bankLine1 = [d.bankName, d.bankBranchName].filter(Boolean).join('　');
  const bankLine2 = [
    d.bankAccountTypeLabel ? `（${d.bankAccountTypeLabel}）` : '',
    d.bankAccount,
    d.bankAccountHolderName,
  ]
    .filter(Boolean)
    .join('　');

  // 控除明細はマイナス表記。取引支払額は税込金額のためプラス記号は付けない。
  const minusYen = (n: number) => `− ${yen(n)}`;

  const subtotal =
    d.transactionPaymentAmount - d.transactionInvoiceAmount - d.systemFeeAmount - d.fixedFeeAmount;
  const hasOffset = d.offsetAmount > 0;
  // 合計は明細から算出して「小計 − 相殺 = 合計」を必ず成立させる(帳票の内訳整合を保証)。
  // 正常データでは subtotal − offset = actual_transfer(= お支払金額)に一致する。
  const total = subtotal - d.offsetAmount;

  return (
    <Page>
      {/* タイトルは画面トップ中央。No.(先頭8桁・下線付き)/発行日は右上。 */}
      <HeaderTable>
        <tbody>
          <tr>
            <td style={{ width: '24%' }} />
            <td style={{ width: '52%', textAlign: 'center', verticalAlign: 'middle' }}>
              <TitleBadge>支払通知書</TitleBadge>
            </td>
            <MetaCell>
              <div style={{ borderBottom: '1px solid #888', paddingBottom: 2 }}>
                No.　{d.id.slice(0, 8)}
              </div>
              <div style={{ marginTop: 2 }}>発行日　{d.issueDate}</div>
            </MetaCell>
          </tr>
        </tbody>
      </HeaderTable>

      {/* recipient (加盟店) / issuer (発行体) */}
      <PartyTable>
        <tbody>
          <tr>
            <RecipientCell>
              <div style={{ fontSize: 17, fontWeight: 'bold' }}>{d.merchantName}　御中</div>
              <RegistrationNumber>
                事業者登録番号：{d.merchantRegistrationNumber}
              </RegistrationNumber>
            </RecipientCell>
            <td style={{ width: '4%' }} />
            <IssuerCell>
              <div style={{ fontSize: 15, fontWeight: 'bold', color: ACCENT_DARK }}>
                {d.issuerName}
              </div>
              <RegistrationNumber>
                事業者登録番号：{d.issuerRegistrationNumber}
              </RegistrationNumber>
            </IssuerCell>
          </tr>
        </tbody>
      </PartyTable>

      {/* info block (下線は本文幅に合わせて約半分まで) */}
      <InfoTable>
        <tbody>
          <InfoRow label="電子マネー">{d.privateMoneyName}</InfoRow>
          <InfoRow label="期間">{`${d.periodFrom} ～ ${d.periodTo}`}</InfoRow>
          <InfoRow label="お支払予定日">{d.scheduledPaymentDate}</InfoRow>
          <InfoRow label="振込先口座">
            {bankLine1 ? <div>{bankLine1}</div> : null}
            {bankLine2 ? <div>{bankLine2}</div> : null}
          </InfoRow>
        </tbody>
      </InfoTable>

      {/* payment amount band */}
      <AmountBand>
        <tbody>
          <tr>
            <AmountLabelCell>お支払金額</AmountLabelCell>
            <AmountValueCell>¥{Math.round(d.paymentAmount).toLocaleString()}−</AmountValueCell>
          </tr>
        </tbody>
      </AmountBand>

      {/* detail table: プラス/マイナス明細を整列し小計→相殺→合計。消費税は右カラム。 */}
      <DetailTable>
        <thead>
          <tr>
            <DetailHead>明細</DetailHead>
            <DetailHead $width="56px">税率</DetailHead>
            <DetailHead $width="140px">金額</DetailHead>
          </tr>
        </thead>
        <tbody>
          <DetailRow label="取引支払額（決済支払額）" rate="" amount={yen(d.transactionPaymentAmount)} />
          <DetailRow
            label="取引請求額（チャージ請求額+決済手数料+ポイント負担請求額）"
            rate=""
            amount={minusYen(d.transactionInvoiceAmount)}
          />
          <DetailRow label="月額システム利用料" rate="10%" amount={minusYen(d.systemFeeAmount)} />
          <DetailRow label="その他固定費" rate="10%" amount={minusYen(d.fixedFeeAmount)} />
          <DetailRow label="小計" rate="" amount={yen(subtotal)} emphasize />
          {hasOffset &&
            (d.offsets.length > 0 ? (
              // 充当先の請求データごとに1行(複数の請求へ充当され得る)。合算は offsetAmount と一致。
              d.offsets.map((o, i) => (
                <React.Fragment key={i}>
                  <DetailRow
                    label={offsetLabel(o.periodFrom, o.periodTo)}
                    rate=""
                    amount={minusYen(o.amount)}
                  />
                </React.Fragment>
              ))
            ) : (
              // 内訳が取れない古いデータ等では合計額のみ(期間なし)で1行にフォールバック。
              <DetailRow label="請求額の相殺" rate="" amount={minusYen(d.offsetAmount)} />
            ))}
          <DetailRow label="合計" rate="" amount={yen(total)} emphasize />
          <DetailRow
            label="うち消費税額（10%対象）"
            rate=""
            amount={yen(d.consumptionTaxAmount)}
            muted
          />
        </tbody>
      </DetailTable>
    </Page>
  );
};

// 一時的にオフスクリーンへ React で描画 → html2canvas でキャプチャ → jsPDF で A4 1ページに貼り付けてDL。
export const downloadPaymentNoticePdf = async (
  d: PaymentNoticeData,
  fileName: string
): Promise<void> => {
  const container = document.createElement('div');
  // display:none だと html2canvas がキャプチャできないため、画面外に配置する。
  container.style.position = 'fixed';
  container.style.left = '-10000px';
  container.style.top = '0';
  container.style.width = `${CAPTURE_WIDTH_PX}px`;
  container.style.background = '#ffffff';
  document.body.appendChild(container);

  try {
    // ReactDOM.render のコールバック後、styled-components のスタイル注入とレイアウトが
    // 確定するのを1フレーム待ってからキャプチャする。
    await new Promise<void>((resolve) => {
      ReactDOM.render(<PaymentNotice data={d} />, container, () => {
        requestAnimationFrame(() => resolve());
      });
    });

    const canvas = await html2canvas(container, { scale: 2, backgroundColor: '#ffffff' });
    const imgData = canvas.toDataURL('image/png');

    const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    // アスペクト比を保ったまま、幅優先で配置。高さが1ページを超える場合は高さ優先に切替える。
    let renderWidth = pageWidth;
    let renderHeight = (canvas.height * renderWidth) / canvas.width;
    if (renderHeight > pageHeight) {
      renderHeight = pageHeight;
      renderWidth = (canvas.width * renderHeight) / canvas.height;
    }
    const offsetX = (pageWidth - renderWidth) / 2;

    pdf.addImage(imgData, 'PNG', offsetX, 0, renderWidth, renderHeight);
    pdf.save(fileName);
  } finally {
    ReactDOM.unmountComponentAtNode(container);
    document.body.removeChild(container);
  }
};
